// Finance module shared UI primitives (2026-08-25).
//
// Small set of drop-in components that every Finance page (Dashboard,
// Transactions, Expenses, Reports, Reconciliation) should compose from
// so a page-level rewrite is a one-file swap instead of a per-page
// duplication of KPI card / section shell / empty-state code.
//
// Exports:
//   FinancePageHeader   Premium header card (eyebrow + title +
//                       description + right-side actions slot).
//   FinanceKpiCard      Unified KPI tile with tinted icon + big value.
//   FinanceSectionCard  White card shell with icon-tile header + slot.
//   FinanceEmptyState   Compact centered empty state with icon + CTA.
//
// All read from `financeTheme.ts` so a token change ripples everywhere.

import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { fin, toneStyles, FinTone } from "./financeTheme";

// ─────────────────────────────────────────────────────────────────────
// FinancePageHeader
// ─────────────────────────────────────────────────────────────────────

export const FinancePageHeader: React.FC<{
  title: string;
  eyebrow?: string;
  description?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
}> = ({ title, eyebrow = "Finance", description, icon, actions, meta }) => (
  <Box sx={{
    bgcolor: fin.color.card,
    border: `1px solid ${fin.color.border}`,
    borderRadius: `${fin.radius.lg}px`,
    boxShadow: fin.shadow.header,
    background: `linear-gradient(90deg, ${fin.color.surfaceBlue} 0%, ${fin.color.card} 60%, ${fin.color.borderSoft} 100%)`,
    px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.25 },
    mb: 2.5,
    display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
  }}>
    {icon && (
      <Box sx={{
        width: 46, height: 46, borderRadius: `${fin.radius.md}px`,
        bgcolor: fin.color.card,
        border: `1px solid ${fin.color.border}`,
        color: fin.color.blue,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 24 },
      }}>{icon}</Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 800, color: fin.color.blue,
        letterSpacing: "0.1em", textTransform: "uppercase",
      }}>
        {eyebrow}
      </Typography>
      <Typography sx={{
        fontSize: { xs: 22, md: 26 }, fontWeight: 800,
        color: fin.color.navy, letterSpacing: "-0.02em", lineHeight: 1.15,
        mt: 0.25,
      }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 13, color: fin.color.muted, mt: 0.4 }}>
          {description}
        </Typography>
      )}
      {meta && (
        <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>{meta}</Box>
      )}
    </Box>
    {actions && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
        {actions}
      </Box>
    )}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────
// FinanceKpiCard
// ─────────────────────────────────────────────────────────────────────

export const FinanceKpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tone?: FinTone;
  hint?: React.ReactNode;
  onClick?: () => void;
}> = ({ label, value, icon, tone = "blue", hint, onClick }) => {
  const t = toneStyles(tone);
  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: fin.color.card,
        border: `1px solid ${fin.color.border}`,
        borderRadius: `${fin.radius.md}px`,
        boxShadow: fin.shadow.card,
        p: 2, minWidth: 0,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
        ...(onClick && {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: fin.shadow.cardHover,
            borderColor: t.fg,
          },
        }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        {icon && (
          <Box sx={{
            width: 34, height: 34, borderRadius: `${fin.radius.sm}px`,
            bgcolor: t.bg, color: t.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { fontSize: 18 },
          }}>{icon}</Box>
        )}
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, color: fin.color.muted,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: 24, fontWeight: 800, color: fin.color.navy,
        lineHeight: 1.1, letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11.5, color: fin.color.muted, mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────
// FinanceSectionCard
// ─────────────────────────────────────────────────────────────────────

export const FinanceSectionCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  tone?: FinTone;
  action?: React.ReactNode;
  subtitle?: React.ReactNode;
  padding?: number;
  children?: React.ReactNode;
  sx?: any;
}> = ({ title, icon, tone = "blue", action, subtitle, padding = 2.25, children, sx }) => {
  const t = toneStyles(tone);
  return (
    <Box sx={{
      bgcolor: fin.color.card,
      border: `1px solid ${fin.color.border}`,
      borderRadius: `${fin.radius.md}px`,
      boxShadow: fin.shadow.card,
      p: padding,
      ...sx,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
        {icon && (
          <Box sx={{
            width: 32, height: 32, borderRadius: `${fin.radius.sm}px`,
            bgcolor: t.bg, color: t.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { fontSize: 18 },
          }}>{icon}</Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: 800, color: fin.color.navy,
            letterSpacing: "-0.01em",
          }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11.5, color: fin.color.muted, mt: 0.15 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Box>
      {children}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────
// FinanceEmptyState
// ─────────────────────────────────────────────────────────────────────

export const FinanceEmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  body?: React.ReactNode;
  tone?: FinTone;
  cta?: { label: string; onClick: () => void; icon?: React.ReactNode };
  minHeight?: number | string;
}> = ({ icon, title, body, tone = "blue", cta, minHeight = 200 }) => {
  const t = toneStyles(tone);
  return (
    <Box sx={{
      minHeight,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      textAlign: "center", py: 3, px: 3,
    }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: `${fin.radius.md}px`,
        bgcolor: t.bg, color: t.fg,
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: 1.5,
        "& svg": { fontSize: 26 },
      }}>{icon}</Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: fin.color.navy, mb: 0.5 }}>
        {title}
      </Typography>
      {body && (
        <Typography sx={{ fontSize: 12.5, color: fin.color.muted, maxWidth: 420, mx: "auto" }}>
          {body}
        </Typography>
      )}
      {cta && (
        <Button
          onClick={cta.onClick}
          startIcon={cta.icon}
          variant="contained"
          size="small"
          sx={{
            mt: 2, textTransform: "none", fontWeight: 800, borderRadius: 2,
            bgcolor: fin.color.blue, boxShadow: "none", px: 2,
            "&:hover": { bgcolor: fin.color.navy, boxShadow: `0 6px 14px rgba(78,120,166,0.28)` },
          }}
        >
          {cta.label}
        </Button>
      )}
    </Box>
  );
};

export { fin, toneStyles } from "./financeTheme";
export type { FinTone } from "./financeTheme";
